import { escapeHtml, urlPerAttributo } from './escape.ts';
import type { RenderContext } from './types.ts';

/**
 * L'involucro brandizzato di ogni email: intestazione con il logo, corpo,
 * piede.
 *
 * Il piede non e' decorazione. Contiene tre cose obbligatorie:
 *   - chi scrive, con indirizzo fisico e partita IVA
 *   - il link per disiscriversi
 *   - il link alle preferenze
 * Senza il link di disiscrizione Gmail e Outlook trattano il messaggio come
 * posta indesiderata, e senza indirizzo fisico la mail e' fuori norma.
 */

/**
 * La riga di anteprima: quel pezzo di testo che i client di posta mostrano
 * accanto all'oggetto, prima che uno apra. Se non la si scrive, mostrano le
 * prime parole dell'HTML, che di solito e' "Annulla iscrizione" o il nome del
 * file del logo. E' nascosta alla vista ma non allo scanner: il blocco di
 * spazi invisibili dopo il testo serve a impedire che il client ci infili
 * comunque l'inizio del corpo.
 */
function rigaAnteprima(testo?: string): string {
  if (!testo || !testo.trim()) return '';
  return `<div style="display: none; max-height: 0; overflow: hidden; mso-hide: all;">${escapeHtml(testo.trim())}</div>
  <div style="display: none; max-height: 0; overflow: hidden; mso-hide: all;">${'&#847;&zwnj;&nbsp;'.repeat(60)}</div>`;
}

export function involucro(corpo: string, ctx: RenderContext, preheader?: string): string {
  const logo = urlPerAttributo(ctx.logoUrl);
  const b = ctx.branding;
  const sede = [b.indirizzo, b.localita].filter(Boolean).join(' - ');

  const piedeDisiscrizione = ctx.senzaIscrizione
    ? `Hai ricevuto questo messaggio perche' ci hai contattato.
       Per non riceverne altri, basta rispondere a questa email.`
    : ctx.anteprima
      ? `<span style="color: #9ca3af;">Annulla iscrizione</span> &nbsp;·&nbsp; <span style="color: #9ca3af;">Gestisci preferenze</span>
       <div style="margin-top: 6px; color: #d1d5db; font-size: 11px;">(nell'anteprima i due link sono disattivati)</div>`
      : `<a href="${urlPerAttributo(ctx.unsubscribeUrl)}" style="color: #6b7280; text-decoration: underline;">Annulla iscrizione</a>
       &nbsp;·&nbsp;
       <a href="${urlPerAttributo(ctx.preferenzeUrl)}" style="color: #6b7280; text-decoration: underline;">Gestisci preferenze</a>`;

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light">
  <title>${escapeHtml(b.nomeBreve)}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f3f4f6;">
  ${rigaAnteprima(preheader)}
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f3f4f6; padding: 32px 16px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; max-width: 600px;">
          <tr>
            <!-- Fondo bianco e non colorato: il logo e' a colori e su un fondo del
                 colore della marca la sua parte rossa sparirebbe. Il colore resta
                 nella striscia in alto. -->
            <td style="background-color: #ffffff; border-top: 6px solid ${ctx.colori.primario}; border-bottom: 1px solid #f3f4f6; padding: 28px 30px 24px 30px; text-align: center;">
              <img src="${logo}" alt="${escapeHtml(b.nomeBreve)}" style="max-width: 200px; height: auto; display: block; margin: 0 auto; border: 0;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 36px 0 20px 0;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
${corpo}
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding: 24px 40px 32px 40px; background-color: #f9fafb; border-top: 1px solid #e5e7eb;">
              <p style="margin: 0 0 10px 0; color: #6b7280; font-size: 13px; line-height: 1.6; text-align: center;">
                <strong style="color: ${ctx.colori.primario};">${escapeHtml(b.ragioneSociale)}</strong><br/>
                ${escapeHtml(sede)}${b.partitaIva ? `<br/>P.IVA: ${escapeHtml(b.partitaIva)}` : ''}
              </p>
              <p style="margin: 0; color: #6b7280; font-size: 12px; line-height: 1.6; text-align: center;">
                ${piedeDisiscrizione}
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export function involucroTesto(corpo: string, ctx: RenderContext): string {
  const b = ctx.branding;
  const sede = [b.indirizzo, b.localita].filter(Boolean).join(' - ');
  const disiscrizione = ctx.senzaIscrizione
    ? 'Hai ricevuto questo messaggio perche\' ci hai contattato. Per non riceverne altri, rispondi a questa email.'
    : ctx.anteprima
      ? 'Annulla iscrizione: (disattivato in anteprima)'
      : `Annulla iscrizione: ${ctx.unsubscribeUrl}`;

  return `${corpo}

--
${b.ragioneSociale}
${sede}${b.partitaIva ? `\nP.IVA: ${b.partitaIva}` : ''}

${disiscrizione}`;
}
